import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

interface Preset {
  name: string;
  min: number;
  max: number;
  description: string;
}

interface IntervalConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (min: number, max: number) => void;
  initialMin?: number;
  initialMax?: number;
}

const IntervalConfigModal: React.FC<IntervalConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialMin = 30,
  initialMax = 60,
}) => {
  const { t } = useTranslation();

  // Definindo presets de forma estável usando useMemo
  const presets: Preset[] = useMemo(() => [
    { name: t('sidebar.preset_fast'), min: 15, max: 30, description: t('sidebar.preset_fast_desc') },
    { name: t('sidebar.preset_moderate'), min: 30, max: 60, description: t('sidebar.preset_moderate_desc') },
    { name: t('sidebar.preset_safe'), min: 60, max: 120, description: t('sidebar.preset_safe_desc') },
  ], [t]); // Depende apenas de 't'

  const getPresetName = useCallback((min: number, max: number) => {
    const found = presets.find(p => p.min === min && p.max === max);
    return found ? found.name : '';
  }, [presets]);

  const [minInterval, setMinInterval] = useState(initialMin);
  const [maxInterval, setMaxInterval] = useState(initialMax);
  const [selectedPreset, setSelectedPreset] = useState(getPresetName(initialMin, initialMax));

  // Efeito para resetar o estado quando o modal abre ou os valores iniciais mudam
  useEffect(() => {
    if (isOpen) {
      setMinInterval(initialMin);
      setMaxInterval(initialMax);
      setSelectedPreset(getPresetName(initialMin, initialMax));
    }
  }, [isOpen, initialMin, initialMax, getPresetName]);

  // Efeito para atualizar o preset selecionado quando os inputs mudam
  useEffect(() => {
    setSelectedPreset(getPresetName(minInterval, maxInterval));
  }, [minInterval, maxInterval, getPresetName]);


  const handlePresetSelect = (preset: Preset) => {
    // Atualiza os estados locais, o que forçará a atualização dos inputs
    setMinInterval(preset.min);
    setMaxInterval(preset.max);
    setSelectedPreset(preset.name);
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    // Se o campo estiver vazio, definimos como 0 temporariamente para evitar NaN
    setMinInterval(isNaN(value) ? 0 : value);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setMaxInterval(isNaN(value) ? 0 : value);
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Usamos os valores atuais do estado
    const min = minInterval;
    const max = maxInterval;

    if (min >= 0 && max >= min) {
      onSave(min, max);
      onClose();
      // Exibir notificação de sucesso após salvar
      toast.success(t('sidebar.interval_saved'));
    } else {
      // Se a validação falhar, exibe o alerta (que já está no código original)
      alert(t('sidebar.interval_error'));
    }
  };

  const isInvalid = minInterval > maxInterval || minInterval < 8 || maxInterval > 300;

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gradient-to-br from-primary/10 to-orange-400/10 via-transparent backdrop-blur-sm dark:bg-black/70"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-2xl rounded-xl bg-white dark:bg-background-dark-elevated shadow-2xl border border-gray-200 dark:border-primary-darkMode/60 max-h-[90vh] overflow-y-auto"
        initial={{ y: 50, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 50, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-primary-darkMode/40 rounded-t-xl">
          <div className="flex flex-col gap-1">
            <h3 className="text-[#181411] dark:text-text-dark-primary tracking-light text-2xl font-bold leading-tight">{t('sidebar.interval_modal_title')}</h3>
            <p className="text-gray-600 dark:text-text-dark-secondary text-sm font-normal leading-normal">{t('sidebar.interval_modal_subtitle')}</p>
          </div>
          <motion.button
            onClick={onClose}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-gray-100 dark:bg-background-dark-elevated2 text-[#181411] dark:text-text-dark-primary transition-colors hover:bg-gray-200 dark:hover:bg-background-dark-elevated3 border border-gray-200 dark:border-primary-darkMode/50 dark:hover:border-primary-darkMode/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:focus-visible:ring-primary-darkMode"
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </motion.button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Warning Box */}
            <motion.div
              className="flex items-start gap-3 p-4 rounded-lg bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <span className="material-symbols-outlined text-yellow-600 dark:text-yellow-400 mt-0.5">warning</span>
              <div className="flex flex-col gap-1">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <span className="font-bold">{t('Aviso importante:')}</span> {t('sidebar.interval_warning')}
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-400/80">
                  * Mínimo de segurança: 8s | Máximo: 300s (5 min)
                </p>
              </div>
            </motion.div>

            {/* Input Fields */}
            <div className="flex w-full flex-wrap items-end gap-4">
              <label className="flex flex-col min-w-40 flex-1">
                <p className="text-[#181411] dark:text-text-dark-primary text-base font-medium leading-normal pb-2">{t('sidebar.interval_min')} (Min 8s)</p>
                <input
                  className={`form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#181411] dark:text-text-dark-primary bg-white dark:bg-background-dark-elevated2 border ${minInterval < 8 || minInterval > maxInterval ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-primary-darkMode/45'} focus:outline-0 focus:ring-2 focus:ring-primary dark:focus:ring-primary-darkMode focus:border-transparent h-12 placeholder:text-gray-400 dark:placeholder:text-text-dark-tertiary p-[15px] text-base font-normal leading-normal`}
                  placeholder="e.g., 30"
                  type="number"
                  min="8"
                  max="300"
                  value={minInterval}
                  onChange={handleMinChange}
                  required
                />
              </label>
              <label className="flex flex-col min-w-40 flex-1">
                <p className="text-[#181411] dark:text-text-dark-primary text-base font-medium leading-normal pb-2">{t('sidebar.interval_max')} (Max 300s)</p>
                <input
                  className={`form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#181411] dark:text-text-dark-primary bg-white dark:bg-background-dark-elevated2 border ${maxInterval > 300 || minInterval > maxInterval ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-primary-darkMode/45'} focus:outline-0 focus:ring-2 focus:ring-primary dark:focus:ring-primary-darkMode focus:border-transparent h-12 placeholder:text-gray-400 dark:placeholder:text-text-dark-tertiary p-[15px] text-base font-normal leading-normal`}
                  placeholder="e.g., 60"
                  type="number"
                  min="8"
                  max="300"
                  value={maxInterval}
                  onChange={handleMaxChange}
                  required
                />
              </label>
            </div>

            {/* Error Message */}
            {(minInterval > maxInterval || minInterval < 8 || maxInterval > 300) && (
              <motion.p
                className="text-sm text-red-500 font-medium"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {minInterval < 8 ? "O tempo mínimo deve ser de pelo menos 8 segundos." :
                  maxInterval > 300 ? "O tempo máximo não pode exceder 300 segundos." :
                    t('sidebar.interval_error')}
              </motion.p>
            )}

            {/* Example */}
            <p className="text-sm text-gray-600 dark:text-text-dark-secondary mt-2">
              <span className="font-semibold">{t('Exemplo:')}</span> {t('sidebar.interval_example', { min: minInterval, max: maxInterval })}
            </p>

            {/* Presets Section */}
            <div className="mt-6">
              <h2 className="text-[#181411] dark:text-text-dark-primary text-lg font-bold leading-tight tracking-[-0.015em] text-left pb-2 pt-4">{t('sidebar.presets_title')}</h2>
              <div className="flex flex-col gap-3">
                <div className="flex h-10 w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-background-dark-elevated2 p-1">
                  {presets.map((preset) => (
                    <motion.button
                      key={preset.name}
                      type="button"
                      onClick={() => handlePresetSelect(preset)}
                      className={`flex cursor-pointer h-full grow items-center justify-center overflow-hidden rounded-md px-4 text-sm font-medium leading-normal transition-all border ${selectedPreset === preset.name
                        ? 'border-primary/20 dark:border-primary-darkMode/60 bg-white dark:bg-background-dark-elevated3 shadow text-[#181411] dark:text-text-dark-primary'
                        : 'border-transparent text-gray-600 dark:text-text-dark-secondary hover:bg-gray-200 dark:hover:bg-background-dark-elevated'
                        }`}
                      whileHover={{ scale: selectedPreset === preset.name ? 1 : 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {preset.name}
                    </motion.button>
                  ))}
                </div>

                {/* Preset Descriptions */}
                <div className="grid grid-cols-3 gap-3">
                  {presets.map((preset) => (
                    <div key={preset.name} className="text-center">
                      <p className={`text-xs font-semibold ${selectedPreset === preset.name ? 'text-primary dark:text-primary-darkMode' : 'text-gray-500 dark:text-text-dark-secondary'}`}>
                        {preset.min}-{preset.max}s
                      </p>
                      <p className={`text-[10px] mt-0.5 ${selectedPreset === preset.name ? 'text-[#181411] dark:text-text-dark-primary' : 'text-gray-500 dark:text-text-dark-tertiary'}`}>
                        {preset.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer / Actions */}
          <div className="flex justify-end p-4 md:p-6 border-t border-gray-200 dark:border-primary-darkMode/40 rounded-b-xl gap-3">
            <motion.button
              type="button"
              onClick={onClose}
              className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-5 bg-gray-100 dark:bg-background-dark-elevated2 text-[#181411] dark:text-text-dark-primary text-sm md:text-base font-bold leading-normal tracking-[0.015em] border border-gray-200 dark:border-primary-darkMode/50 transition-colors hover:bg-gray-200 dark:hover:bg-background-dark-elevated3 dark:hover:border-primary-darkMode/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:focus-visible:ring-border-dark-light"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="truncate">{t('modals.cancel')}</span>
            </motion.button>
            <motion.button
              type="submit"
              className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-5 bg-gradient-primary text-white text-sm md:text-base font-bold leading-normal tracking-[0.015em] transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: isInvalid ? 1 : 1.05 }}
              whileTap={{ scale: isInvalid ? 1 : 0.95 }}
              disabled={isInvalid}
            >
              <span className="truncate">{t('sidebar.save_changes')}</span>
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default IntervalConfigModal;